import React, { useState, MouseEvent } from 'react';
import { Grid } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../redux/storeExports';
import { selectActiveGrant } from '../../../../redux/product/ProductSlice'
import { ProfileSelectors, triggerTodoListAction } from 'shared/redux/profile/ProfileSlice';
import { styles, TODO_ITEMS } from './DashboardTodoConstants';
import { TodoCard } from './DashboardTodoCard';

const DashboardTodoList: React.FC = () => {
  const user = useAppSelector(ProfileSelectors.selectProfile);
  const brokersRequireRemoval = useAppSelector(ProfileSelectors.selectBrokersRequireRemoval);
  const grant = useAppSelector(selectActiveGrant)
  
  const isPeopleFindersCompleted = !(Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('peoplefinders'));
  const isInteliusCompleted = !(Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('intelius'));
  const isDataBrokerCommunicationsCompleted = !!user?.metadata?.todo_actions?.some(a => a.action_category === 'data_comm_acknowledgement' && a.action === 'acknowledge');

  const shouldShowPeopleFinders = (Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('peoplefinders')) ||
    !!user?.metadata?.todo_actions?.some(a => a.action_category === 'broker' && a.broker_name === 'peoplefinders' && a.action === 'confirmator');

  const shouldShowIntelius = (Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('intelius')) ||
    !!user?.metadata?.todo_actions?.some(a => a.action_category === 'broker' && a.broker_name === 'intelius' && a.action === 'confirmator');

  const getLatestAction = (category: string, brokerName?: string) => {
    const actions = user?.metadata?.todo_actions || [];
    return [...actions].reverse().find(a => {
      if (a.action_category !== category) return false;
      if (a.action_category === 'broker') {
        return a.broker_name === brokerName;
      }
      return true;
    });
  };

  const isPeopleFindersPending = !isPeopleFindersCompleted && getLatestAction('broker', 'peoplefinders')?.action === 'later';
  const isInteliusPending = !isInteliusCompleted && getLatestAction('broker', 'intelius')?.action === 'later';
  const isDataBrokerCommunicationsPending = !isDataBrokerCommunicationsCompleted && getLatestAction('data_comm_acknowledgement')?.action === 'later';

  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [menuWidth, setMenuWidth] = useState<number>();
 
  const getIsCompleted = (itemId: number) => {
    if (itemId === 1) return isPeopleFindersCompleted;
    if (itemId === 2) return isInteliusCompleted;
    if (itemId === 3) return isDataBrokerCommunicationsCompleted;
    return false;
  };

  const getIsPending = (itemId: number) => {
    if (itemId === 1) return isPeopleFindersPending;
    if (itemId === 2) return isInteliusPending;
    if (itemId === 3) return isDataBrokerCommunicationsPending;
    return false;
  };

  const getIsNoActionNeeded = (itemId: number) => {
    if (itemId === 1) return !shouldShowPeopleFinders;
    if (itemId === 2) return !shouldShowIntelius;
    return false;
  };

  const handleOpen = (event: MouseEvent<HTMLElement>, id: number) => {
    if (openId === id) {
      handleClose();
    } else {
      setMenuWidth(event.currentTarget.offsetWidth);
      setAnchorEl(event.currentTarget);
      setOpenId(id);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenId(null);
  };

  const handleLater = (action_cat: string, broker_name?: string) => {
    if (action_cat == 'broker' && broker_name) {
      return dispatch(triggerTodoListAction({ action_category: 'broker', action: 'later', broker_name })).unwrap();
    } else if (action_cat == 'data_comm_acknowledgement') {
      return dispatch(triggerTodoListAction({ action_category: 'data_comm_acknowledgement', action: 'later' })).unwrap();
    }
  };

  const getCallbacks = (itemId: number) => {
    if (itemId === 1) {
      return {
        onConfirm: () => dispatch(triggerTodoListAction({ action_category: 'broker', action: 'confirmator', broker_name: 'peoplefinders' })).unwrap(),
        onTriggerOptout: () => dispatch(triggerTodoListAction({ action_category: 'broker', action: 'removal', broker_name: 'peoplefinders' })).unwrap(),
        onLater: () => handleLater('broker', 'peoplefinders'),
      };
    }
    if (itemId === 2) {
      return {
        onConfirm: () => dispatch(triggerTodoListAction({ action_category: 'broker', action: 'confirmator', broker_name: 'intelius' })).unwrap(),
        onLater: () => handleLater('broker', 'intelius'),
      };
    }
    if (itemId === 3) {
      return {
        onConfirm: () => dispatch(triggerTodoListAction({ action_category: 'data_comm_acknowledgement', action: 'acknowledge' })).unwrap(),
        onLater: () => handleLater('data_comm_acknowledgement'),
      };
    }
    return {};
  };

  const visibleTodoItems = TODO_ITEMS.filter(item => {
    if (item.id === 2 || item.id === 1) {
      return grant?.grant_source !== 'corporate_grant'
    }
    return true;
  });

  return (
    <Grid container sx={styles.container}>
      {visibleTodoItems.map((item, index) => (
        <TodoCard
          key={item.id}
          item={item}
          isOpen={openId === item.id}
          isCompleted={getIsCompleted(item.id)}
          isPending={getIsPending(item.id)}
          isNoActionNeeded={getIsNoActionNeeded(item.id)}
          onOpen={handleOpen}
          anchorEl={anchorEl}
          menuWidth={menuWidth}
          onClose={handleClose}
          index={index}
          total={visibleTodoItems.length}
          {...getCallbacks(item.id)}
        />
      ))}
    </Grid>
  );
};

export default DashboardTodoList;
